import { useCallback } from 'react';
import { getIn, useFormikContext } from 'formik';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';

import { ensureDefined } from '@e-socna/utils';
import type { EMPTY_STRING } from '@e-socna/empty';
import type { UnitId } from '@e-socna/data-layer';

import { getTouchedError } from '../../../../utils/get-touched-error.util';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useContextualizedFields } from '../../../../hooks/useContextualizedFields.hook';
import { QUANTITY_VALUE_FIELD_STEP } from '../../../../constants/number-field-steps.constant';
import { IdSelect } from '../../../../components/IdSelect';
import type { CostListCmp_Cost } from '../../../../components/CostList/hooks/useFindAllCostsQuery.hook';
import type { IdOption } from '../../../../components/IdSelect';

import type {
  CostUsageForm_CostUsage,
  OptionalCostUsageFormContext,
  RequiredCostUsageFormContext,
} from '../../fragments/cost-usage.fragment';

interface Props {
  readonly allCosts: readonly CostListCmp_Cost[];
  readonly costUsage: CostUsageForm_CostUsage;
  readonly formContext?: string;
  readonly index: number;
}

export const Item = ({
  allCosts,
  costUsage,
  formContext,
  index,
}: Readonly<Props>) => {
  const { units } = useReferencesContext();

  const { handleBlur, handleChange, errors, setFieldValue, touched } =
    useFormikContext<
      OptionalCostUsageFormContext | RequiredCostUsageFormContext
    >();

  const { cost } = ensureDefined({
    cost: allCosts.find((cost) => cost._id === costUsage.costId),
  });

  const indexAsString = index.toFixed();

  const fieldsBase = useContextualizedFields<
    Readonly<{
      quantity: CostUsageForm_CostUsage['quantity'];
    }>
  >(
    {
      quantity: {
        id: `cost-usage-${indexAsString}-quantity`,
        name: `costUsages[${indexAsString}].quantity`,
      },
    },
    formContext,
  );

  const fields = {
    ...fieldsBase,
    quantityValue: {
      id: `${fieldsBase.quantity.id}-value`,
      name: `${fieldsBase.quantity.name}.value`,
      value: fieldsBase.quantity.value.value,
    },
    quantityUnitId: {
      id: `${fieldsBase.quantity.id}-unitId`,
      name: `${fieldsBase.quantity.name}.unitId`,
      value: fieldsBase.quantity.value.unitId,
    },
  };

  const handleUnitIdChange = useCallback(
    (id: UnitId | typeof EMPTY_STRING) => {
      void setFieldValue(fields.quantityUnitId.name, id);
    },
    [fields.quantityUnitId.name, setFieldValue],
  );

  return (
    <Paper key={cost._id} sx={{ width: '100%', p: 2 }}>
      <Typography sx={{ mb: 4 }} variant="h6">
        {cost.code} - {cost.label}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ sm: 6, xs: 12 }}>
          <TextField
            error={
              !!(
                getIn(errors, fields.quantityValue.name) &&
                getIn(touched, fields.quantityValue.name)
              )
            }
            fullWidth
            helperText={getTouchedError(
              errors,
              touched,
              fields.quantityValue.name,
            )}
            id={fields.quantityValue.id}
            label="Quantité"
            name={fields.quantityValue.name}
            onBlur={handleBlur}
            onChange={handleChange}
            required
            slotProps={{
              htmlInput: {
                min: QUANTITY_VALUE_FIELD_STEP,
                step: QUANTITY_VALUE_FIELD_STEP,
              },
            }}
            type="number"
            value={fields.quantityValue.value}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }}>
          <IdSelect<UnitId, IdOption<UnitId>>
            disabled
            error={
              !!(
                getIn(errors, fields.quantityUnitId.name) &&
                getIn(touched, fields.quantityUnitId.name)
              )
            }
            helperText={
              !!(
                getIn(errors, fields.quantityUnitId.name) &&
                getIn(touched, fields.quantityUnitId.name)
              ) && (getIn(errors, fields.quantityUnitId.name) as string)
            }
            id={fields.quantityUnitId.id}
            label="Unité"
            name={fields.quantityUnitId.name}
            onBlur={handleBlur}
            onChange={handleUnitIdChange}
            options={units.map((unit) => ({
              id: unit._id,
              label: unit.label,
            }))}
            required
            value={fields.quantityUnitId.value as UnitId}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
